import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  DateInput,
  SelectInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  Toolbar,
  SaveButton,
} from 'react-admin';
import { Dialog, DialogTitle, DialogContent, IconButton, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const AssignmentToolbar = () => (
  <Toolbar
    sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '16px 24px',
      borderTop: '1px solid #e0e0e0',
    }}
  >
    <SaveButton />
  </Toolbar>
);

interface AssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: number;
  recordId?: number;
  isEdit?: boolean;
}

export const AssignmentDialog: React.FC<AssignmentDialogProps> = ({
  open,
  onClose,
  equipmentId,
  recordId,
  isEdit = false,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [record, setRecord] = useState(null);
  const [drivers, setDrivers] = useState([]);

  const conditionTypes = [
    { id: 'EXCELLENT', name: 'Excellent' },
    { id: 'GOOD', name: 'Good' },
    { id: 'FAIR', name: 'Fair' },
    { id: 'POOR', name: 'Poor' },
    { id: 'DAMAGED', name: 'Damaged' },
  ];

  // Load drivers when dialog opens
  useEffect(() => {
    if (open) {
      dataProvider
        .getList('drivers', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'id', order: 'ASC' },
          filter: {},
        })
        .then(({ data }) => {
          const driverChoices = data.map(driver => ({
            id: driver.id,
            name: driver.user?.name || `Driver ${driver.id}`,
          }));
          setDrivers(driverChoices);
        })
        .catch(error => {
          console.error('Error loading drivers:', error);
          notify('Error loading drivers', { type: 'error' });
        });
    }
  }, [open, dataProvider, notify]);

  // Load record for edit mode
  useEffect(() => {
    if (isEdit && recordId && open) {
      true;
      dataProvider
        .getOne('equipment-assignments', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
        })
        .catch(error => {
          console.error('Error loading assignment record:', error);
          notify('Error loading assignment record', { type: 'error' });
        })
        .finally(() => {
          false;
        });
    } else if (!isEdit) {
      setRecord(null);
    }
  }, [isEdit, recordId, open, dataProvider, notify]);

  const handleSubmit = async (data: any) => {
    true;
    try {
      const formattedData = {
        ...data,
        equipmentId: Number(equipmentId),
      };

      if (isEdit && recordId) {
        await dataProvider.update('equipment-assignments', {
          id: recordId,
          data: formattedData,
        });
        notify('Assignment record updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('equipment-assignments', {
          data: formattedData,
        });
        notify('Assignment record created successfully', { type: 'success' });
      }

      refresh();
      onClose();
    } catch (error) {
      console.error('Assignment submission error:', error);
      notify(
        `Error ${isEdit ? 'updating' : 'creating'} assignment record: ${
          error.message || 'Unknown error'
        }`,
        { type: 'error' }
      );
    } finally {
      false;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEdit
          ? translate('resources.equipment_assignments.edit.title')
          : translate('resources.equipment_assignments.create.title')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SimpleForm
          onSubmit={handleSubmit}
          record={
            record || {
              assignedDate: new Date().toISOString().split('T')[0],
            }
          }
          toolbar={<AssignmentToolbar />}
        >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="driverId"
                choices={drivers}
                validate={[required()]}
                fullWidth
                label="Driver"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="condition"
                choices={conditionTypes}
                fullWidth
                label="Condition"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateInput
                source="assignedDate"
                validate={[required()]}
                fullWidth
                label="Assigned Date"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput source="mileageOut" fullWidth label="Mileage Out" />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateInput source="returnedDate" fullWidth label="Returned Date" />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput source="mileageIn" fullWidth label="Mileage In" />
            </Grid>
            <Grid item xs={12}>
              <TextInput source="notes" fullWidth multiline rows={3} label="Notes" />
            </Grid>
          </Grid>
        </SimpleForm>
      </DialogContent>
    </Dialog>
  );
};
